/**
 * A small Python -> JavaScript translator covering the Karel teaching subset:
 * `def`, `while`, `if/elif/else`, `for ... in range(...)`, boolean operators,
 * comments, and Karel command calls. This lets Python programs run on the
 * existing step-based `KarelEvalEngine` without a full in-browser interpreter.
 */

export class PythonSyntaxError extends Error {
  line: number;
  constructor(message: string, line: number) {
    super(message);
    this.name = "PythonSyntaxError";
    this.line = line;
  }
}

const KEYWORD_MAP: [RegExp, string][] = [
  [/\bTrue\b/g, "true"],
  [/\bFalse\b/g, "false"],
  [/\bNone\b/g, "null"],
  [/\band\b/g, "&&"],
  [/\bor\b/g, "||"],
  [/\bnot\b/g, "!"],
];

function countIndent(line: string): number {
  let indent = 0;
  for (const ch of line) {
    if (ch === " ") indent += 1;
    else if (ch === "\t") indent += 4;
    else break;
  }
  return indent;
}

function stripComment(line: string): string {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (ch === "#" && !inSingle && !inDouble) return line.slice(0, i);
  }
  return line;
}

function translateExpression(expr: string): string {
  let result = expr.replace(/\.append\s*\(/g, ".push(");
  for (const [pattern, replacement] of KEYWORD_MAP) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

function translateFor(stripped: string, line: number): string {
  const match = stripped.match(/^for\s+([A-Za-z_]\w*)\s+in\s+(.+):$/);
  if (!match) throw new PythonSyntaxError("Unsupported for-loop syntax", line);
  const variable = match[1];
  const iterable = match[2].trim();
  const rangeMatch = iterable.match(/^range\s*\((.*)\)$/);
  if (!rangeMatch) {
    // `for item in some_list:` iterates list elements (AP CSP FOR EACH).
    return `for (const ${variable} of (${translateExpression(iterable)})) {`;
  }
  const args = rangeMatch[1].split(",").map((a) => a.trim());
  if (args.length === 1) {
    return `for (let ${variable} = 0; ${variable} < (${translateExpression(args[0])}); ${variable}++) {`;
  }
  if (args.length === 2) {
    return `for (let ${variable} = (${translateExpression(args[0])}); ${variable} < (${translateExpression(args[1])}); ${variable}++) {`;
  }
  if (args.length === 3) {
    return `for (let ${variable} = (${translateExpression(args[0])}); ${variable} < (${translateExpression(args[1])}); ${variable} += (${translateExpression(args[2])})) {`;
  }
  throw new PythonSyntaxError("range() takes 1 to 3 arguments", line);
}

export function translatePython(code: string): string {
  const rawLines = code.replace(/\r\n?/g, "\n").split("\n");
  const out: string[] = [];
  // Root sentinel. Real block headers have indent >= 0, so the loop guard
  // (`length > 1`) keeps the sentinel from ever being closed.
  const indentStack: number[] = [-1];

  // A block header at indent `h` owns a body at indent > h and ends when a
  // line at indent <= h appears. `inclusive` closes sibling blocks at the same
  // indent; `else`/`elif` pass false so they stay attached to their `if`.
  const closeTo = (indent: number, inclusive: boolean): void => {
    while (indentStack.length > 1) {
      const top = indentStack[indentStack.length - 1];
      if (inclusive ? top >= indent : top > indent) {
        indentStack.pop();
        out.push("}");
      } else {
        break;
      }
    }
  };

  for (let i = 0; i < rawLines.length; i++) {
    const lineNumber = i + 1;
    const withoutComment = stripComment(rawLines[i]);
    if (withoutComment.trim() === "") continue;

    const indent = countIndent(withoutComment);
    const stripped = withoutComment.trim();

    const isElse = /^else\s*:$/.test(stripped);
    const isElif = stripped.startsWith("elif ");

    closeTo(indent, !isElse && !isElif);

    if (isElse) {
      out.push("} else {");
      continue;
    }
    if (isElif) {
      const condition = stripped.slice(5, -1).trim();
      out.push(`} else if (${translateExpression(condition)}) {`);
      continue;
    }

    if (stripped.startsWith("def ")) {
      const match = stripped.match(/^def\s+([A-Za-z_]\w*)\s*\((.*)\)\s*:$/);
      if (!match)
        throw new PythonSyntaxError(
          "Malformed function definition",
          lineNumber,
        );
      out.push(`function ${match[1]}(${match[2].trim()}) {`);
      indentStack.push(indent);
      continue;
    }

    if (stripped.startsWith("while ")) {
      const condition = stripped.slice(6, -1).trim();
      out.push(`while (${translateExpression(condition)}) {`);
      indentStack.push(indent);
      continue;
    }

    if (stripped.startsWith("if ")) {
      const condition = stripped.slice(3, -1).trim();
      out.push(`if (${translateExpression(condition)}) {`);
      indentStack.push(indent);
      continue;
    }

    if (stripped.startsWith("for ")) {
      out.push(translateFor(stripped, lineNumber));
      indentStack.push(indent);
      continue;
    }

    if (stripped === "pass") {
      out.push(";");
      continue;
    }

    if (/:\s*$/.test(stripped)) {
      throw new PythonSyntaxError("Unsupported Python block", lineNumber);
    }

    out.push(translateExpression(stripped));
  }

  while (indentStack.length > 1) {
    indentStack.pop();
    out.push("}");
  }

  return out.join("\n");
}
